import { getCurrentUser, fetchUserAttributes } from '@aws-amplify/auth';
import type { AuthUser } from '@aws-amplify/auth';

import type { UserAttributes } from '../types';

/**
 * Get the current authenticated user
 */
export async function getCurrentAuthUser(): Promise<AuthUser | null> {
  try {
    const user = await getCurrentUser();
    return user;
  } catch (error: any) {
    // Handle development environment where Auth is not configured
    if (error?.message?.includes('Auth UserPool not configured')) {
      console.warn(
        '⚠️ AWS Auth not configured - using mock user for development'
      );
      return {
        username: 'dev-user',
        userId: 'dev-123',
        signInDetails: undefined,
      } as AuthUser;
    }

    console.error('Error getting current user:', error);
    return null;
  }
}

/**
 * Get user attributes from Cognito
 */
export async function getUserAttributes(): Promise<UserAttributes> {
  try {
    const attributes = await fetchUserAttributes();
    return attributes;
  } catch (error: any) {
    // Handle development environment where Auth is not configured
    if (error?.message?.includes('Auth UserPool not configured')) {
      console.warn(
        '⚠️ AWS Auth not configured - using mock user data for development'
      );
      return {
        name: 'Development User',
        given_name: 'Development',
        family_name: 'User',
        email: 'dev@choco.com',
        email_verified: 'true',
      };
    }

    // Handle missing Identity Pool (non-critical error)
    if (
      error?.message?.includes('IdentityPool') &&
      error?.message?.includes('not found')
    ) {
      // Identity Pool is optional - this is not a critical error
      // Only log in development mode
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug(
          'Identity Pool not found (this is optional and non-critical):',
          error.message
        );
      }
      // Try to get attributes without Identity Pool
      try {
        const attributes = await fetchUserAttributes();
        return attributes;
      } catch (retryError) {
        // If it still fails, return empty attributes
        return {};
      }
    }

    // Only log actual errors, not Identity Pool issues
    if (!error?.message?.includes('IdentityPool')) {
    console.error('Error fetching user attributes:', error);
    }
    return {};
  }
}

/**
 * Check if user has specific attribute
 */
export function hasUserAttribute(
  attributes: UserAttributes,
  attributeName: string
): boolean {
  return Boolean(attributes[attributeName]);
}

/**
 * Extract user display name from attributes
 */
export function getUserDisplayName(attributes: UserAttributes): string {
  return (
    attributes.name ||
    `${attributes.given_name || ''} ${attributes.family_name || ''}`.trim() ||
    attributes.email ||
    'User'
  );
}

/**
 * Check if user email is verified
 */
export function isEmailVerified(attributes: UserAttributes): boolean {
  return attributes.email_verified === 'true';
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phoneNumber?: string): string {
  if (!phoneNumber) return '';

  // Remove any non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // Format US phone numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return phoneNumber;
}

/**
 * Parse Cognito error messages for user-friendly display
 */
export function parseAuthError(error: any): string {
  if (!error) return 'An unknown error occurred';

  const errorMessage = error.message || error.toString();

  // Common Cognito error mappings
  const errorMappings: Record<string, string> = {
    UserNotFoundException: 'User not found. Please check your credentials.',
    NotAuthorizedException: 'Incorrect username or password.',
    UserNotConfirmedException:
      'Account not confirmed. Please check your email for confirmation instructions.',
    PasswordResetRequiredException:
      'Password reset required. Please reset your password.',
    UserLambdaValidationException: 'Invalid user data provided.',
    InvalidPasswordException: 'Password does not meet requirements.',
    LimitExceededException: 'Too many attempts. Please try again later.',
    TooManyRequestsException: 'Too many requests. Please try again later.',
    CodeMismatchException: 'Invalid verification code.',
    ExpiredCodeException: 'Verification code has expired.',
    InvalidParameterException: 'Invalid parameters provided.',
    UsernameExistsException: 'An account with this email already exists.',
  };

  // Check for specific error types
  const matchedError = Object.entries(errorMappings).find(([errorType]) =>
    errorMessage.includes(errorType)
  );

  if (matchedError) {
    return matchedError[1];
  }

  // Return original message if no mapping found
  return errorMessage;
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (!/(?=.*[a-z])/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/(?=.*\d)/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (!/(?=.*[@$!%*?&])/.test(password)) {
    errors.push(
      'Password must contain at least one special character (@$!%*?&)'
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Generate a secure random string for state parameters
 */
export function generateRandomString(length: number = 32): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  return Array.from({ length }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('');
}
